import { useCallback, useMemo, useRef, useState } from 'react'
import { useAppContext } from '../../../../redux/slices/appContext'
import { useOrionDomainQuery, useOrionIdentifier, postOrionDomainQuery } from '../../../../api/coreData'
import { buildOrionRequest, buildChildRequest, buildFilteredChildRequest } from '../lib/buildOrionRequest'
import { transformResponse, transformChildResponse } from '../lib/transformResponse'
import { ENTITY_SCOPABLE_GROUPINGS } from '../lib/constants'

/**
 * Finds a row by _rowId in a row tree (depth-first).
 * Returns the row reference so it can be mutated in place.
 */
function findRow (rows, rowId) {
  for (const row of rows) {
    if (row._rowId === rowId) return row
    if (row.data) {
      const found = findRow(row.data, rowId)
      if (found) return found
    }
  }
  return null
}

export function usePortfolioExplorerQuery (config) {
  const { clientId, availableDates } = useAppContext()
  const asOfDate = availableDates?.mainDate ?? availableDates?.max
  const isLazy = !config.fetchAllLevels && config.groupings.length > 1

  const { data: orionIdentity } = useOrionIdentifier(clientId)
  const entity = orionIdentity?.entity
  const entityIds = orionIdentity?.entityIds

  const request = useMemo(() => {
    if (!asOfDate || !entity || !entityIds || config.columns.length === 0) return null
    return buildOrionRequest(config, {
      asOfDate,
      maxGroupingDepth: isLazy ? 1 : undefined,
      entity,
      entityIds
    })
  }, [config, asOfDate, isLazy, entity, entityIds])

  const { data: rawData, isFetching, error, refetch } = useOrionDomainQuery(
    'reporting/scope',
    clientId,
    request,
    { enabled: false }
  )

  const hasMoreLevels = isLazy && config.groupings.length > 1

  const baseRows = useMemo(() => {
    if (!rawData) return []
    const { rows } = transformResponse(rawData, {
      hasMoreLevels,
      columnInstances: config.columns,
      entity,
      entityIds
    })
    return rows
  }, [rawData, hasMoreLevels, config.columns, entity, entityIds])

  const [rowTree, setRowTree] = useState([])
  const rowTreeRef = useRef([])
  const lastBaseRowsRef = useRef(null)

  // Intentional setState-during-render to sync mutable row tree with derived baseRows.
  // This avoids stale-closure issues that useEffect would introduce for the ref+state
  // pairing needed by expandRow. React batches the extra render. Revisit if warnings appear.
  if (baseRows !== lastBaseRowsRef.current) {
    lastBaseRowsRef.current = baseRows
    const cloned = JSON.parse(JSON.stringify(baseRows))
    rowTreeRef.current = cloned
    if (rowTree !== cloned) {
      setRowTree(cloned)
    }
  }

  const expandRow = useCallback(async (row) => {
    if (!clientId || !asOfDate || row._childrenLoaded) return

    const target = findRow(rowTreeRef.current, row._rowId)
    if (!target) return

    target._subRowsFetching = true
    setRowTree([...rowTreeRef.current])

    try {
      const groupingIndex = config.groupings.indexOf(row.grouping)
      const childDepthIndex = groupingIndex >= 0 ? groupingIndex + 1 : 1
      const remainingGroupingCount = config.groupings.length - childDepthIndex
      const isScopable = ENTITY_SCOPABLE_GROUPINGS.has(row.grouping)

      const shouldFetchAll = remainingGroupingCount <= 2 ||
        (isScopable && remainingGroupingCount <= 3)
      const childMaxDepth = shouldFetchAll ? undefined : 1
      const childHasMoreLevels = !shouldFetchAll && remainingGroupingCount > 1

      let childRequestBody
      if (isScopable) {
        childRequestBody = buildChildRequest(config, {
          asOfDate,
          depthIndex: childDepthIndex,
          maxGroupingDepth: childMaxDepth,
          entity: row.grouping,
          entityIds: [row.groupEntityId]
        })
      } else {
        const ancestorEnt = row._ancestorEntity || entity
        const ancestorIds = row._ancestorEntityIds || entityIds
        childRequestBody = buildFilteredChildRequest(config, {
          asOfDate,
          depthIndex: childDepthIndex,
          maxGroupingDepth: childMaxDepth,
          entity: ancestorEnt,
          entityIds: ancestorIds,
          filterGrouping: row.grouping,
          filterName: row.name
        })
      }

      const childResponse = await postOrionDomainQuery('reporting/scope', clientId, childRequestBody)

      const scopeEntity = isScopable ? row.grouping : (row._ancestorEntity || entity)
      const scopeEntityIds = isScopable ? [row.groupEntityId] : (row._ancestorEntityIds || entityIds)

      let childRows = transformChildResponse(childResponse, {
        hasMoreLevels: childHasMoreLevels,
        columnInstances: config.columns,
        entity: scopeEntity,
        entityIds: scopeEntityIds
      })

      if (!isScopable) {
        const match = childRows.find(r =>
          (row.groupEntityId != null && r.groupEntityId === row.groupEntityId) ||
          r.name === row.name
        )
        if (match?.data) {
          childRows = match.data
        }
      }

      target.data = childRows.length > 0 ? childRows : undefined
      target._childrenLoaded = true
    } catch (err) {
      console.error('Failed to expand row', err)
      target.data = undefined
      // Known limitation: marking _childrenLoaded prevents retry on transient failure.
      // TODO: allow retry by resetting this flag, e.g. via a "retry" affordance on the row.
      target._childrenLoaded = true
    } finally {
      target._subRowsFetching = false
      setRowTree([...rowTreeRef.current])
    }
  }, [clientId, asOfDate, config, entity, entityIds])

  return {
    rows: rowTree,
    isFetching,
    error,
    refetch,
    expandRow,
    hasClient: !!clientId,
    isReady: request !== null
  }
}
